import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ✅ Fetch settings
export const useSettings = (token: string | null) => {
  return useQuery({
    queryKey: ['settings', token],
    queryFn: async () => {
      if (!token) throw new Error('Authentication required');
      
      try {
        const response = await axios.get(`${API_URL}/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        if (error.response?.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }
        if (!navigator.onLine) {
          throw new Error('No internet connection.');
        }
        throw new Error(error.response?.data?.error || 'Failed to load settings');
      }
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 3, // ✅ Add retry
  });
};

// ✅ Update settings
export const useUpdateSettings = (token: string | null) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settingsData: any) => {
      if (!token) throw new Error('No token provided');
      const response = await axios.put(`${API_URL}/settings`, settingsData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    onSuccess: () => {
      // ✅ Invalidate settings cache
      queryClient.invalidateQueries({ queryKey: ['settings', token] });
    },
  });
};

// ============  Documentation ============
/**
 * @fileoverview Custom React Query hooks for user settings management
 * @module hooks/useSettings
 * 
 * This module provides hooks for:
 * - Fetching user settings (wave amount, frequency, language, theme, etc.)
 * - Updating user settings
 * 
 * Settings include:
 * - Wave amount (paycheck amount)
 * - Payment frequency (weekly, biweekly, monthly)
 * - Language preference (en/es)
 * - Theme color
 * - Income sources configuration
 * - Subscription tier
 */

/**
 * Hook to fetch user settings
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseQueryResult} React Query result object containing:
 * - data: {
 *     waveAmount: number,
 *     frequency: 'weekly' | 'biweekly' | 'monthly',
 *     language: 'en' | 'es',
 *     isSetupComplete: boolean,
 *     subscriptionTier: 'free' | 'standard' | 'premium',
 *     themeColor: string,
 *     incomeSources: Array<{id, name, amount, frequency, color}>
 *   }
 * - isLoading: boolean - Loading state
 * - error: Error | null - Error object if request failed
 * 
 * @example
 * const { data: settings, isLoading } = useSettings(token);
 * if (settings) {
 *   console.log(`Wave Amount: $${settings.waveAmount}`);
 *   console.log(`Language: ${settings.language}`);
 * }
 * 
 * @features
 * - Automatic retry (3 attempts)
 * - Long cache time (5 minutes stale, 10 minutes garbage collection)
 * - Better error messages for auth and server errors
 * - Offline detection
 */

/**
 * Hook to update user settings
 * 
 * @param {string | null} token - JWT authentication token
 * 
 * @returns {UseMutationResult} React Query mutation object
 * 
 * @example
 * const updateSettings = useUpdateSettings(token);
 * 
 * updateSettings.mutate({
 *   waveAmount: 1500,
 *   frequency: 'biweekly',
 *   language: 'es',
 *   themeColor: 'green'
 * }, {
 *   onSuccess: () => console.log("Settings updated!"),
 *   onError: (error) => console.error(error)
 * });
 * 
 * @features
 * - Automatic cache invalidation
 * - Settings refetch after successful update
 */